import React, { useState } from "react";
import PropTypes from "prop-types";
import { confirmable, createConfirmation } from "react-confirm";
import { Season } from "@mysnowscape/snow-lib";

import ModalWrapper from "./ModalWrapper";
import XMarkIcon from "../../resources/route-builder/x-mark.svg";
import { resolveRouteColorPalette } from "../Maps/ActiveRoutes/routeColor";
import { persistedRouteType } from "../../utils/routeType";
import CheckBox from "../Inputs/CheckBox";

function RouteItem({ route, inGrid = false }) {
  const stopCount = route.jobIds?.length ?? route.jobs?.length ?? 0;
  const isTemporary = persistedRouteType(route) === "temporary";

  return (
    <li
      className={`flex min-h-9 items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-white ${
        inGrid ? "border border-slate-100/80 bg-white/60" : ""
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="flex h-4 w-4 shrink-0 items-center justify-center"
          aria-hidden
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{
              backgroundColor: resolveRouteColorPalette(route.color).hex,
            }}
          />
        </span>
        <span className="truncate text-sm font-semibold text-slate-800">
          {route.name}
        </span>
        {isTemporary ? (
          <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
            One-time
          </span>
        ) : null}
      </div>
      <span className="shrink-0 text-xs text-slate-400">
        {stopCount} {stopCount === 1 ? "stop" : "stops"}
      </span>
    </li>
  );
}

RouteItem.propTypes = {
  route: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
    season: PropTypes.string,
    jobIds: PropTypes.array,
    jobs: PropTypes.array,
    expiresAt: PropTypes.string,
  }).isRequired,
  inGrid: PropTypes.bool,
};

const PrintRoutesConfirmDialog = ({ show, proceed, options = {} }) => {
  const [includeMap, setIncludeMap] = useState(false);

  if (!show) return null;

  const { routes = [] } = options;
  const count = routes.length;
  const snowRoutes = routes.filter((r) => r.season === Season.snow);
  const landscapeRoutes = routes.filter((r) => r.season === Season.landscape);
  const hasBoth = snowRoutes.length > 0 && landscapeRoutes.length > 0;

  return (
    <ModalWrapper clickOff={() => proceed(false)} bgOpacity="40">
      <div
        className="relative z-50 mx-4 flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-routes-dialog-title"
        data-testid="print-routes-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3
              id="print-routes-dialog-title"
              className="text-lg font-bold text-snow-primary"
            >
              Print Routes
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Print {count === 1 ? "this route" : `these ${count} routes`}:
            </p>
          </div>
          <button
            type="button"
            data-testid="print-routes-close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            onClick={() => proceed(false)}
            aria-label="Close"
          >
            <img src={XMarkIcon} alt="" className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[min(26rem,60dvh)] overflow-y-auto px-6 py-4">
          {hasBoth ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {snowRoutes.length > 0 ? (
                <div className="flex flex-col">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-snow-blue-3">
                      Snow Routes ({snowRoutes.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50/60 p-1 list-none max-h-72 overflow-y-auto">
                    {snowRoutes.map((route) => (
                      <RouteItem key={route._id} route={route} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {landscapeRoutes.length > 0 ? (
                <div className="flex flex-col">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-landscape-primary">
                      Landscape Routes ({landscapeRoutes.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50/60 p-1 list-none max-h-72 overflow-y-auto">
                    {landscapeRoutes.map((route) => (
                      <RouteItem key={route._id} route={route} />
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : routes.length > 4 ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-2 list-none max-h-72 overflow-y-auto">
              {routes.map((route) => (
                <RouteItem key={route._id} route={route} inGrid />
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50/60 p-1 list-none max-h-72 overflow-y-auto">
              {routes.map((route) => (
                <RouteItem key={route._id} route={route} />
              ))}
            </ul>
          )}
        </div>

        {/* Map Option */}
        <div className="border-t border-slate-100 bg-slate-50/40 px-6 py-3">
          <CheckBox
            name="includeMap"
            label="Include Map"
            description="(increases download time)"
            checked={includeMap}
            onChange={setIncludeMap}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-3.5">
          <button
            type="button"
            data-testid="print-routes-cancel"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
            onClick={() => proceed(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="print-routes-confirm"
            className="rounded-xl bg-snow-blue-3 px-5 py-2 text-sm font-bold text-white hover:opacity-80 active:opacity-40"
            onClick={() => proceed({ confirmed: true, includeMap })}
          >
            Download PDF
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

PrintRoutesConfirmDialog.propTypes = {
  show: PropTypes.bool,
  proceed: PropTypes.func,
  options: PropTypes.shape({
    routes: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        color: PropTypes.string,
        season: PropTypes.string,
        jobIds: PropTypes.array,
        jobs: PropTypes.array,
        expiresAt: PropTypes.string,
      }),
    ),
  }),
};

const confirmableModal = confirmable(PrintRoutesConfirmDialog);
const confirm = createConfirmation(confirmableModal);

export function confirmPrintRoutes(options = {}) {
  return confirm({ options });
}

export default PrintRoutesConfirmDialog;
